import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ClinicCard from "@/components/ClinicCard";
import { LOCATIONS } from "@/data/locations";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useLang } from "@/i18n/LanguageContext";
import { SlidersHorizontal, X, MapPin } from "lucide-react";

const ANY = "any";

export default function Compare() {
  const { t } = useLang();
  const [clinics, setClinics] = useState([]);
  const [filters, setFilters] = useState({ countries: [], procedures: [], certifications: [] });
  const [country, setCountry] = useState(ANY);
  const [procedure, setProcedure] = useState(ANY);
  const [certification, setCertification] = useState(ANY);
  const [maxPrice, setMaxPrice] = useState([10000]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/clinics/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (country !== ANY) params.country = country;
    if (procedure !== ANY) params.procedure = procedure;
    if (certification !== ANY) params.certification = certification;
    params.max_price = maxPrice[0];
    api.get("/clinics", { params }).then((r) => setClinics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [country, procedure, certification, maxPrice]);

  const reset = () => { setCountry(ANY); setProcedure(ANY); setCertification(ANY); setMaxPrice([10000]); };
  const active = country !== ANY || procedure !== ANY || certification !== ANY || maxPrice[0] !== 10000;

  const grouped = clinics.reduce((acc, c) => {
    const key = `${c.city}, ${c.country}`;
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("compare.eyebrow")}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">{t("compare.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("compare.sub")}</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-8 items-start">
        <aside className="lg:sticky lg:top-24 rounded-2xl border border-border bg-white p-6 space-y-6" data-testid="clinic-filters">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-bold"><SlidersHorizontal className="w-4 h-4 text-primary" /> {t("compare.filters")}</div>
            {active && <button onClick={reset} className="text-xs text-primary flex items-center gap-1" data-testid="reset-filters-btn"><X className="w-3 h-3" /> {t("compare.reset")}</button>}
          </div>

          <div className="space-y-2">
            <Label>{t("compare.country")}</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-11" data-testid="filter-country"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>{t("compare.anyCountry")}</SelectItem>{filters.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("compare.procedure")}</Label>
            <Select value={procedure} onValueChange={setProcedure}>
              <SelectTrigger className="h-11" data-testid="filter-procedure"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>{t("compare.anyProcedure")}</SelectItem>{filters.procedures.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("compare.certification")}</Label>
            <Select value={certification} onValueChange={setCertification}>
              <SelectTrigger className="h-11" data-testid="filter-certification"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>{t("compare.anyCertification")}</SelectItem>{filters.certifications.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            <Label>{t("compare.maxPrice")} <span className="text-primary font-semibold">${maxPrice[0].toLocaleString()}</span></Label>
            <Slider value={maxPrice} onValueChange={setMaxPrice} min={200} max={10000} step={100} data-testid="filter-max-price" />
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex flex-wrap gap-2 mb-5" data-testid="location-tabs">
            {[ANY, ...filters.countries].map((c) => (
              <button
                key={c}
                onClick={() => setCountry(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${country === c ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground/70 hover:border-primary"}`}
                data-testid={`location-tab-${c === ANY ? "all" : c.toLowerCase().replace(/\s/g, "-")}`}
              >
                {c === ANY ? t("compare.allLocations") : c}
              </button>
            ))}
          </div>

          <p className="text-sm text-muted-foreground mb-4" data-testid="clinic-results-count">{loading ? t("common.loading") : (clinics.length === 1 ? t("compare.foundOne") : t("compare.found", { n: clinics.length }))}</p>
          {!loading && clinics.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground">{t("compare.noMatch")}</p>
              <Button variant="outline" className="rounded-full mt-4" onClick={reset}>{t("compare.clearFilters")}</Button>
            </div>
          ) : (
            <div className="space-y-9">
              {Object.entries(grouped).map(([loc, list]) => (
                <div key={loc} data-testid={`location-group-${loc.split(",")[0].toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-primary" />
                    <h2 className="font-heading font-bold text-lg">{loc}</h2>
                    <span className="text-xs text-muted-foreground">({list.length})</span>
                    {(() => { const m = LOCATIONS.find((x) => x.city === list[0].city); return m ? <Link to={`/locations/${m.slug}`} className="text-xs text-primary hover:underline ml-auto" data-testid={`location-guide-link-${m.slug}`}>{t("compare.cityGuide", { city: list[0].city })}</Link> : null; })()}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {list.map((c, i) => <ClinicCard key={c.id} clinic={c} index={i} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
